import { StandardEvents } from '@shopify/events';

/**
 * Cart-page only: soft-disable B2B Checkout until WSH paints prices, then unlock.
 * Unlocks when WSH prices are already on the page — does not wait for a later
 * text delta. Cart-line events do not reset the hard timeout. Morph putting
 * Liquid `disabled` back on the button is undone if prices are ready.
 * Product adds on the B2B cart page reload after the add succeeds — Horizon
 * morph replaces WSH prices with list prices, and WSH only repaints on load.
 * Quantity update/remove stay on the gate path (no reload).
 * Does not intercept checkout clicks — the WSH app embed owns checkout.
 */

const GATED_SELECTOR = 'button.cart__checkout-button[data-wsh-price-gate="true"]';
const STATUS_SELECTOR = '[data-wsh-checkout-status]';
const READY_TIMEOUT_MS = 4000;
const POLL_MS = 150;

/** @type {boolean} */
let locked = false;
/** @type {number} */
let unlockDeadline = 0;
/** @type {number | undefined} */
let pollTimer;
/** @type {number | undefined} */
let timeoutTimer;
/** @type {MutationObserver | undefined} */
let attributeObserver;
/** @type {boolean} */
let reloadingForWsh = false;
/** @type {number | undefined} */
let reloadDebounceTimer;
/** @type {Promise<unknown>[]} */
const pendingAddPromises = [];

/**
 * @returns {boolean}
 */
function isCartPage() {
  const templateName = window.Theme?.template?.name || '';
  if (templateName === 'cart' || templateName.startsWith('cart.')) return true;

  return /\/cart\/?$/.test(window.location.pathname);
}

/**
 * B2B cart page — gate markup is missing on an empty cart, so use the wholesale flag.
 * @returns {boolean}
 */
function isWholesaleCartPage() {
  if (!isCartPage()) return false;

  return Boolean(document.querySelector('.cart__checkout-button[data-wsh-wholesale="true"]'));
}

/**
 * @returns {HTMLButtonElement[]}
 */
function gatedButtons() {
  return Array.from(document.querySelectorAll(GATED_SELECTOR)).filter(
    (el) => el instanceof HTMLButtonElement && el.dataset.cartEmpty !== 'true'
  );
}

/**
 * WSH finished painting (or decided list price is the wholesale price).
 * @returns {boolean}
 */
function isPricingReady() {
  for (const node of document.querySelectorAll('.wcp-cart-total')) {
    if (node.textContent?.trim()) return true;
  }

  for (const node of document.querySelectorAll('.wcp-original-cart-total')) {
    if (!(node instanceof HTMLElement)) continue;

    if ((node.getAttribute('style') || '').includes('line-through')) return true;
    if (window.getComputedStyle(node).textDecorationLine.includes('line-through')) return true;
  }

  const hash = window.wcp_data?.discounted_hash;
  if (!hash || typeof hash !== 'object') return false;
  if (hash.formatted_wcp_total != null) return true;
  if (hash.discount_value === false) return true;

  return false;
}

/**
 * @param {boolean} nextLocked
 */
function applyLockUi(nextLocked) {
  window.wpd_prevent_checkout = nextLocked === true;

  for (const button of gatedButtons()) {
    if (nextLocked) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  }

  for (const status of document.querySelectorAll(STATUS_SELECTOR)) {
    if (status instanceof HTMLElement) status.hidden = !nextLocked;
  }
}

function clearTimers() {
  if (pollTimer !== undefined) {
    window.clearInterval(pollTimer);
    pollTimer = undefined;
  }

  if (timeoutTimer !== undefined) {
    window.clearTimeout(timeoutTimer);
    timeoutTimer = undefined;
  }
}

function requestWshRefresh() {
  if (typeof window.wcpRefreshCartTotal !== 'function') return;

  try {
    window.wcpRefreshCartTotal();
  } catch (error) {
    console.warn('[cart-wsh-price-gate] wcpRefreshCartTotal failed', error);
  }
}

function unlock() {
  locked = false;
  unlockDeadline = 0;
  clearTimers();
  applyLockUi(false);
}

function startWatchers() {
  if (pollTimer === undefined) {
    pollTimer = window.setInterval(() => {
      if (!locked) return;

      if (isPricingReady() || Date.now() >= unlockDeadline) {
        unlock();
      }
    }, POLL_MS);
  }

  if (timeoutTimer === undefined && unlockDeadline > 0) {
    const remaining = Math.max(0, unlockDeadline - Date.now());
    timeoutTimer = window.setTimeout(() => {
      timeoutTimer = undefined;
      if (locked) unlock();
    }, remaining);
  }
}

/**
 * Lock until prices are ready. If already waiting, keep the original deadline.
 */
function ensureLocked() {
  if (!isCartPage() || gatedButtons().length === 0) return;

  if (isPricingReady()) {
    unlock();
    return;
  }

  if (!locked) {
    locked = true;
    unlockDeadline = Date.now() + READY_TIMEOUT_MS;
    applyLockUi(true);
    requestWshRefresh();
  }

  startWatchers();
}

/**
 * After qty/morph: unlock immediately if WSH already painted; otherwise gate
 * without resetting an in-flight timeout.
 */
function onCartPricingMayChange() {
  if (!isCartPage() || gatedButtons().length === 0) return;

  if (isPricingReady()) {
    unlock();
    return;
  }

  ensureLocked();
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isCartLineAdd(event) {
  return 'action' in event && event.action === 'add';
}

/**
 * @param {unknown} result
 * @returns {boolean}
 */
function addPromiseFailed(result) {
  if (!result || typeof result !== 'object') return false;

  const record = /** @type {{ userErrors?: unknown, detail?: { didError?: unknown } }} */ (result);
  const errors = record.userErrors;
  if (Array.isArray(errors) && errors.length > 0) return true;
  if (record.detail?.didError === true) return true;

  return false;
}

/**
 * Horizon morphs cart HTML with list prices. WSH only paints wholesale prices
 * on a full load, so reload after a successful add on the B2B cart page.
 * Debounces so helium batch adds (multiple `action: 'add'` events) share one reload.
 * @param {Event} event
 */
function reloadAfterSuccessfulAdd(event) {
  const promise = 'promise' in event ? event.promise : null;
  if (promise && typeof promise.then === 'function') {
    pendingAddPromises.push(promise);
  }

  if (reloadDebounceTimer !== undefined) {
    window.clearTimeout(reloadDebounceTimer);
  }

  reloadDebounceTimer = window.setTimeout(() => {
    reloadDebounceTimer = undefined;
    flushReloadAfterAdds();
  }, 150);
}

/**
 * @returns {Promise<void>}
 */
async function flushReloadAfterAdds() {
  if (reloadingForWsh) return;

  reloadingForWsh = true;

  const promises = pendingAddPromises.splice(0);

  if (promises.length > 0) {
    try {
      const results = await Promise.allSettled(promises);
      const anySuccess = results.some((result) => {
        if (result.status !== 'fulfilled') return false;

        return !addPromiseFailed(result.value);
      });

      if (!anySuccess) {
        reloadingForWsh = false;
        onCartPricingMayChange();
        return;
      }
    } catch {
      reloadingForWsh = false;
      onCartPricingMayChange();
      return;
    }
  }

  window.location.reload();
}

/**
 * @param {Event} event
 */
function onCartLinesUpdate(event) {
  if (!isWholesaleCartPage()) return;

  if (isCartLineAdd(event)) {
    reloadAfterSuccessfulAdd(event);
    return;
  }

  onCartPricingMayChange();
}

function observeLockAttributes() {
  if (attributeObserver) return;

  attributeObserver = new MutationObserver(() => {
    if (!isCartPage()) return;

    const buttons = gatedButtons();
    if (buttons.length === 0) return;

    const htmlLocked = buttons.some(
      (button) => button.disabled || button.getAttribute('aria-busy') === 'true'
    );

    if (!htmlLocked) return;

    if (isPricingReady()) {
      unlock();
      return;
    }

    ensureLocked();
  });

  attributeObserver.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-busy', 'data-wsh-price-gate'],
  });
}

function boot() {
  if (!isCartPage() || gatedButtons().length === 0) return;

  observeLockAttributes();
  ensureLocked();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
document.addEventListener(StandardEvents.cartDiscountUpdate, onCartPricingMayChange);
document.addEventListener('shopify:section:load', onCartPricingMayChange);
window.addEventListener('pageshow', onCartPricingMayChange);
